import React, { useState, useEffect } from "react";
import config from "config.json";
import useFetch from "hooks/useFetch";
import { useNavigate } from "react-router-dom";
import PaymentsTable from "components/BookingsTable/PaymentsTable";
import QRCode from "qrcode.react";

interface Occupant {
    fecha_nac: string;
    dni: string;
    apellido_nombre: string;
    condicion: string;
    codigo_habitacion: string;
}

interface Reservation {
    es_confirmacion: boolean;
    hotel: string;
    desde: string;
    hasta: string;
    ocupantes: Occupant[];
    nombre_hotel: string;
}

interface Payments {
    marca: string;
    monto: number;
    estado_pago_enum: string;
    motivo_rechazo: string;
    fecha_hora: string;
    codigo_autoriza: string;
}

interface FetchReservationData {
    id: number;
    fecha_inicio: string;
    fecha_fin: string;
    monto_pactado: number;
    senia: number;
    medio_pago: string;
    fecha_hora_creacion: string;
    estado: string;
    motivo_rechazo_enum: string;
    detalle_rechazo: string;
    motivo_cancelacion: string;
    medio_tarjeta: string;
    pagos: Payments[];
    pre_reserva_request: Reservation;
    nombre_hotel: string;
    uuid_check_in_out: string;
    estado_check: string;
    check_in_time: string;
    check_out_time: string;
    estado_confirmacion: string;
    datos_completos_reserva: {
        pre_reserva_request: Reservation;
    }
    sub_estado:string;
}

const EditReservation: React.FC = () => {
    const [reservation, setReservation] = useState<Reservation | null>(null);
    const [payments, setPayments] = useState<Payments[]>([]);
    const [allData, setAllData] = useState<FetchReservationData>({} as FetchReservationData);
    const [isCancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelReservationRequest, setCancelReservationRequest] = useState({
        id_reserva: '',
        motivo: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [modalMessage, setModalMessage] = useState<string | null>(null);
    const [editReservationRequest, setEditReservationRequest] = useState<Reservation | null>(null);
    const [errorFetchingReservation, setErrorFetchingReservation] = useState<string | null>(null);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [editedOccupants, setEditedOccupants] = useState<Occupant[]>([]);
    const [editReservationCode, setEditReservationCode] = useState<string>('');
    const [callEditWithoutPayments, setCallEditWithoutPayments] = useState(false);

    //manejo de estado de muestra de qr
    const [mostrarQr, setMostrarQr] = useState(false);

    const router = useNavigate();
    const id = new URLSearchParams(document.location.search).get("reservationId");

    const { data: cancelData, isLoading: isCancelLoading, error: cancelError, fetchData: fetchCancel } = useFetch(
        'PUT',
        `${config.backend_url}/setia/reservas/cancelar`,
        cancelReservationRequest,
        {
            'Content-Type': 'application/json',
            'SetiaAuthorization': `${sessionStorage.getItem("user_token")}`
        }
    );

    const { data: reservationData, error: reservationError, fetchData: fetchReservationData } = useFetch<FetchReservationData>(
        'GET',
        `${config.backend_url}/setia/reservas/get/${id}`,
        null,
        {
            'Content-Type': 'application/json',
            'SetiaAuthorization': `${sessionStorage.getItem("user_token")}`
        }
    );

    const { data: editRequestData, error: editRequestError, fetchData: fetchEditRequest } = useFetch(
        'POST',
        `${config.backend_url}/setia/reservas/solicitar_modificacion/${id}`,
        editReservationRequest,
        {
            'Content-Type': 'application/json',
            'SetiaAuthorization': `${sessionStorage.getItem("user_token")}`
        }
    );

    const { data: editWithoutPaymentsData, error: editWithoutPaymentsError, fetchData: fetchEditWithoutPayments } = useFetch(
        'POST',
        `${config.backend_url}/setia/reservas/modificar_sin_pago/${id}`,
        { uuid_reserva: editReservationCode },
        {
            'Content-Type': 'application/json',
            'SetiaAuthorization': `${sessionStorage.getItem("user_token")}`
        }
    );

    function formatearFechaYHora(fechaISO:string) {
        const fecha = new Date(fechaISO);
        const dia = String(fecha.getDate()).padStart(2, '0');
        const mes = String(fecha.getMonth() + 1).padStart(2, '0');
        const anio = fecha.getFullYear();
        const horas = String(fecha.getUTCHours()).padStart(2, '0');
        const minutos = String(fecha.getUTCMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${anio} ${horas}:${minutos}`;
    }

    useEffect(() => {
        fetchReservationData();
    }, []);

    useEffect(() => {
        if (reservationError) {
            setErrorFetchingReservation("Error al cargar los datos de la reserva");
        } else if (reservationData) {
            // Clonar los datos de la reserva para no modificar el objeto original
            const formattedReservation = {
                ...reservationData.datos_completos_reserva.pre_reserva_request,
                desde: reservationData.datos_completos_reserva.pre_reserva_request.desde.split('T')[0],
                hasta: reservationData.datos_completos_reserva.pre_reserva_request.hasta.split('T')[0],
                ocupantes: reservationData.datos_completos_reserva.pre_reserva_request.ocupantes.map((occupant) => ({
                    ...occupant,
                    fecha_nac: occupant.fecha_nac.split('T')[0] // Formatear la fecha de nacimiento
                }))
            };
    
            setReservation(formattedReservation as Reservation);
            setEditedOccupants(formattedReservation.ocupantes);
            setPayments(reservationData.pagos);
            setAllData(reservationData);
        }
    }, [reservationData, reservationError]);

    useEffect(() => {
        if (editRequestError) {
            setModalMessage("Error al editar la reserva");
        } else if (editRequestData) {
            const responseEditRequest = editRequestData as any;
            setEditReservationCode(responseEditRequest.codigo_reserva);
        }
    }, [editRequestData, editRequestError]);

    useEffect(() => {
        if (editReservationCode) {
            fetchEditWithoutPayments();
        }
    }, [editReservationCode]);

    useEffect(() => {
        if (editWithoutPaymentsError) {
            setModalMessage("Error al editar la reserva");
        } else if (editWithoutPaymentsData) {
            setModalMessage("Reserva editada correctamente");
        }
    }, [editWithoutPaymentsData, editWithoutPaymentsError]);

    useEffect(() => {
        if (cancelData && !cancelError) {
            setSuccessMessage('Reserva cancelada con éxito.');
        } else if (cancelError) {
            setErrorMessage('Ocurrió un error al cancelar la reserva.');
        }
    }, [cancelData, cancelError]);

    const handleCancel = (reservation: FetchReservationData) => {
        setCancelModalOpen(true);
        setSuccessMessage('');
        setErrorMessage('');
        setCancelReservationRequest({
            id_reserva: reservation.id.toString(),
            motivo: '',
        });
    };

    const handleCancelReservation = () => {
        fetchCancel();
    };

    const handleOpenEditModal = () => {
        setEditModalOpen(true);
    };

    const handleOccupantChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const updatedOccupants = editedOccupants.map((occupant, i) =>
            i === index ? { ...occupant, [name]: value } : occupant
        );
        setEditedOccupants(updatedOccupants);
    };

    const handleSaveEdit = () => {
        if (reservation) {
            setEditReservationRequest({
                ...reservation,
                ocupantes: editedOccupants,
            });
            setEditModalOpen(false);
        }
    };

    useEffect(() => {
        if (editReservationRequest) {
            fetchEditRequest();
        }
    }, [editReservationRequest]);

    const handleEditModalMessage = () => {
        setModalMessage(null);
        setEditModalOpen(false);
        router('/account-savelists');
    };

    const renderQRCode = (reservationData: FetchReservationData) => {

        // Solo mostrar el QR si la reserva está CONFIRMADA y la confirmación es TOTAL
        if (reservationData.estado !== 'CONFIRMADA' || reservationData.estado_confirmacion !== 'DEFINITIVA' ) {
            return null; // Si no se cumplen las condiciones, no se muestra el QR
        }


        // Determinar el tipo de check-in o check-out
        let type = '';
        if (reservationData.estado_check === null && reservationData.fecha_inicio === new Date().toISOString().split('T')[0] ) {

            type = 'in'; // Si el estado es null y es la fecha de inicio, es un check-in
        } else if (reservationData.estado_check === 'CHECKIN') {
            type = 'out'; // Si ya se hizo el check-in, es para el check-out
        } else if (reservationData.estado_check === 'CHECKOUT') {
            return null; // Si ya se hizo el check-out, no mostramos el QR
        }else{
            return null
        }

        // URL para el check-in/check-out
        const qrUrl = `https://turismo_setia.fire-bots.com/checkin?type=${type}&id=${reservationData.uuid_check_in_out}`;

        return (
            <>
                {!mostrarQr &&
                    <div className="flex flex-col justify-center items-center">
                        <button onClick={() => setMostrarQr(true)} className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-full focus:outline-none focus:shadow-outline">
                            Mostrar QR
                        </button>
                    </div>
                }
                {mostrarQr &&
                    <div className="flex flex-col justify-center items-center">
                        <div className="flex justify-center my-4">
                            <div className="flex flex-col items-center p-4 border rounded-lg">
                                <QRCode value={qrUrl} size={200} />
                                <p className="mt-2 text-sm">Mostra este código para {type === 'in' ? 'hacer check-in' : 'hacer check-out'}</p>
                            </div>
                        </div>
                        <button onClick={() => setMostrarQr(false)} className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-full focus:outline-none focus:shadow-outline">
                            Ocultar QR
                        </button>
                    </div>
                }

            </>
        );
    };


    const renderBookingBalance = (booking: any) => {
        if (!booking || !Array.isArray(booking.pagos)) {
            return (
                <div className="flex justify-center items-center text-center">
                    <div>
                        <p className="text-red-500">No hay información de pagos disponible.</p>
                    </div>
                </div>
            );
        }

        let sumPagos = 0;
        booking.pagos.forEach((pago: any) => {
            if (pago.estado_pago_enum === 'ACEPTADO') {
                sumPagos += Number(pago.monto);
            }
        });
        const handleCompletePayment = () => {
            let totalAmount = booking.monto_pactado - sumPagos;

            sessionStorage.setItem("complete_payment_total", `${totalAmount}`);
            sessionStorage.setItem("partial_payment", "false");
            router(`/complete-payment?reservationId=${id}`);
        };

        const handlePartialPayment = () => {
            let totalAmount = booking.monto_pactado - sumPagos;

            sessionStorage.setItem("complete_payment_total", `${totalAmount}`);
            sessionStorage.setItem("partial_payment", "true");
            router(`/partial-payment?reservationId=${id}`);
        };

        if (booking.monto_pactado - sumPagos > 0 && sessionStorage.getItem("semaphore") === "2") {
            {
                return (
                    <div className="d-flex justify-center items-center text-center">
                        <div>
                            <p className="text-black pt-4">El monto total de tu reserva es de: <span className="font-semibold text-blue-600 font-semibold">{booking.monto_pactado.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span> </p>
                        </div>
                        <div>
                            <p className="text-black pt-4">Tu reserva tiene un saldo pendiente de : <span className="font-semibold text-red-500 font-semibold">{(booking.monto_pactado - sumPagos).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span></p>
                        </div>
                        <div>
                            <button onClick={handleCompletePayment} className="mt-3 ml-4  bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-full ">Pagar {(booking.monto_pactado - sumPagos).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</button>
                            <button onClick={handlePartialPayment} className="mt-3 ml-4  bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-full "> Realizar pago parcial</button>
                        </div>
                    </div>
                );
            }
        } else if (booking.monto_pactado - sumPagos === 0 && sessionStorage.getItem("semaphore") === "2") {
            return (
                <div className="flex justify-center items-center text-center">
                    <div>
                        <p className="text-green-500 pt-4">Ya pagaste el total de tu reserva de {sumPagos.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                    </div>
                </div>
            )
        } else if (booking.monto_pactado - sumPagos > 0 && sessionStorage.getItem("semaphore") !== "2") {
            return (
                <div className="flex justify-center items-center text-center">
                    <div>
                        <div>
                            <p className="text-black pt-4">El monto total de tu reserva es de: <span className="font-semibold text-blue-600 font-semibold">{booking.monto_pactado.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span> </p>
                        </div>
                        <div>
                            <p className="text-black pt-4">Tu reserva tiene un saldo pendiente de : <span className="font-semibold text-red-500 font-semibold">{(booking.monto_pactado - sumPagos).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</span></p>
                        </div>
                        <div className="w-full p-8 bg-gray-300 mt-3 rounded-xl">
                            <p className="text-red-500 pt-4">No podes completar el pago de tu reserva</p>
                            <p className="text-red-500 pt-4">Tu cuenta requiere que subas documentación por favor hacelo desde: Home {">"} Alerta de documentacion pendiente</p>
                            <button onClick={() => router('/account-documents')} className="mt-3 ml-4  bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-full ">Ir a documentacion</button>
                        </div>
                    </div>
                </div>
            )
        }


    };

    if (!reservation) return <div>Cargando...</div>;

    if (errorFetchingReservation) {
        return (
            <div className="container mx-auto my-4 p-4">
                <p className="text-red-500 text-center">{errorFetchingReservation}</p>
                <div className="flex justify-center mt-4">
                    <button
                        onClick={() => router('/account-savelists')}
                        className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-full focus:outline-none focus:shadow-outline"
                    >
                        Volver a Reservas
                    </button>
                </div>
            </div>
        );
    }

    function getDifferenceInDays(startDate: Date, endDate: Date) {
        const differenceInTime = endDate.getTime() - startDate.getTime();
        const differenceInDays = differenceInTime / (1000 * 3600 * 24); // Convertir a días
        return Math.round(differenceInDays); // Redondear al número entero más cercano
    }

    return (
        <>
            <div>
                <div className="container mx-auto my-4">
                    <div className="">
                        <h1 className="text-2xl font-semibold mb-4">Estado de Cuenta</h1>
                    </div>

                    {allData && renderQRCode(allData)}

                    <div className=" mx-auto my-4 ">

                        <form className="bg-white rounded-xl border px-8 pt-6 pb-8 mb-4 overflow-auto">
                            <h2 className="text-2xl font-semibold mb-4">Tu reserva {reservationData?.id} en {reservationData?.nombre_hotel}:</h2>
                            <p>Por {getDifferenceInDays(new Date(reservation.desde), new Date(reservation.hasta))} noches</p>
                            <div className="mb-4 flex">
                                <div>
                                    <label className="block text-gray-700 text-sm font-bold mb-2">
                                        Desde:
                                    </label>
                                    <input
                                        type="date"
                                        name="desde"
                                        min={new Date().toISOString().split('T')[0]}
                                        lang="es-ES"
                                        value={reservation.desde}
                                        disabled
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    />
                                </div>
                                <div className="ml-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2">
                                        Hasta:
                                    </label>
                                    <input
                                        type="date"
                                        name="hasta"
                                        min={new Date().toISOString().split('T')[0]}
                                        lang="es-ES"
                                        value={reservation.hasta}
                                        disabled
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    />
                                </div>
                                <div className="flex items-center pt-7 mx-4">
                                    {
                                        (reservationData && reservationData.estado === "CONFIRMADA" && reservationData.sub_estado === "CONFIRMADA_DEFINITIVA_CHECKIN" && reservationData.estado_check === 'CHECKIN') &&    
                                            <p  className="ml-2"> Hora Checkin: {formatearFechaYHora(reservationData.check_in_time)} </p>

                                    }

                                    {
                                        (reservationData && reservationData.estado === "CONFIRMADA" && reservationData.sub_estado === "CONFIRMADA_DEFINITIVA_CHECKOUT" && reservationData.estado_check === 'CHECKOUT') &&  
                                             <p className="ml-2"> Hora Checkout: {formatearFechaYHora(reservationData.check_out_time)} </p>
                                    }
                                </div>
                            </div>

                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Huespedes:
                            </label>

                            <table className="table-auto w-full overflow-auto">
                                <thead>
                                    <tr>
                                        <th className="border px-4 py-2">Nombre y Apellido</th>
                                        <th className="border px-4 py-2">Fecha de Nacimiento</th>
                                        <th className="border px-4 py-2">DNI</th>
                                        <th className="border px-4 py-2">Condición</th>
                                        <th className="border px-4 py-2">Código de Habitación</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reservation.ocupantes.map((occupant, index) => (
                                        <tr key={index}>
                                            <td className="border px-4 py-2">{occupant.apellido_nombre}</td>
                                            <td className="border px-4 py-2">{occupant.fecha_nac}</td>
                                            <td className="border px-4 py-2">{occupant.dni}</td>
                                            <td className="border px-4 py-2">{occupant.condicion}</td>
                                            <td className="border px-4 py-2">{occupant.codigo_habitacion}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </form>

                        {modalMessage && (
                            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                                <div className="bg-white p-6 rounded shadow-2xl">
                                    <p>{modalMessage}</p>
                                    <div className="flex justify-center">
                                        <button
                                            onClick={handleEditModalMessage}
                                            className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-full focus:outline-none focus:shadow-outline mt-4 "
                                        >
                                            Volver a mis reservas
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isCancelModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-4 rounded shadow-lg">
                        <h2 className="text-xl mb-4">Cancelar Reserva</h2>

                        <p className="mb-4">
                            ¿Estás seguro de cancelar tu reserva ?
                        </p>
                        <input
                            required
                            disabled={successMessage !== ''}
                            type="text"
                            placeholder="Motivo de cancelación"
                            className="mb-4 p-2 border rounded-full w-full"
                            value={cancelReservationRequest.motivo}
                            onChange={(e) => setCancelReservationRequest({ ...cancelReservationRequest, motivo: e.target.value })}
                        />
                        <div className="flex justify-center">
                            <button
                                className="bg-blue-500 text-white px-4 py-2 rounded-full mr-2"
                                onClick={() => setCancelModalOpen(false)}
                            >
                                Cerrar
                            </button>
                            <button
                                disabled={successMessage !== '' || cancelReservationRequest.motivo === ''}
                                className={`bg-red-500 text-white px-4 py-2 rounded-full ${cancelReservationRequest.motivo === '' ? 'opacity-50' : ''}`}
                                onClick={handleCancelReservation}
                            >
                                Cancelar Reserva
                            </button>
                        </div>
                        <div className="flex justify-center">
                            {errorMessage && <p className="text-red-500 mt-2">{errorMessage}</p>}
                            {successMessage && <p className="text-green-500 mt-2">{successMessage}</p>}
                        </div>
                    </div>
                </div>
            )}
            
            <div className="mb-4 flex justify-center space-x-20">
                <div className="flex flex-col text-center">
                    <p className="font-bold">Monto total pagado</p>
                    <p className="font-normal">{reservationData?.senia.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                </div>
                <div className="flex flex-col text-center">
                    <p className="font-semibold">Monto adeudado</p>
                    <p className="font-normal">
                        {
                            (reservationData) && (reservationData.monto_pactado - reservationData.senia).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                        }
                    </p>
                </div>
                <div className="flex flex-col text-center">
                    <p className="font-semibold">Monto Total Reserva</p>
                    <p className="font-normal">{reservationData?.monto_pactado.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>
                </div>
            </div>

            <div className="container d-flex justify-center text-center pb-10">
                {payments.length > 0 && <PaymentsTable payments={payments} />}
                {allData && renderBookingBalance(allData)}
            </div>

            <div className="container d-flex justify-center items-center text-center py-4">
                <button onClick={handleOpenEditModal} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-full focus:outline-none focus:shadow-outline">
                    Editar reserva
                </button>
                {/* <button onClick={() => handleCancel(allData)} className="bg-red-500 hover:bg-red-700 text-white py-2 px-4 rounded-full focus:outline-none focus:shadow-outline ml-4">
                    Cancelar reserva
                </button> */}
            </div>

            {isEditModalOpen && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl h-auto max-h-[80vh] overflow-y-auto">
                        <h2 className="text-2xl font-semibold mb-4 text-center">Editar Información de Reserva</h2>
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
                            <div className="hidden md:block">
                                <table className="w-full">
                                    <thead>
                                        <tr>
                                            <th className="border px-4 py-2">Nombre y Apellido</th>
                                            <th className="border px-4 py-2">Fecha de Nacimiento</th>
                                            <th className="border px-4 py-2" style={{ minWidth: '150px' }}>DNI</th>
                                            <th className="border px-4 py-2">Condición</th>
                                            <th className="border px-4 py-2">Código de Habitación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editedOccupants.map((occupant, index) => (
                                            <tr key={index}>
                                                <td className="border px-4 py-2">
                                                    <input
                                                        type="text"
                                                        name="apellido_nombre"
                                                        value={occupant.apellido_nombre}
                                                        onChange={(e) => handleOccupantChange(index, e)}
                                                        className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                    />
                                                </td>
                                                <td className="border px-4 py-2">
                                                    <input
                                                        type="date"
                                                        name="fecha_nac"
                                                        disabled
                                                        value={occupant.fecha_nac}
                                                        onChange={(e) => handleOccupantChange(index, e)}
                                                        className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline bg-gray-200"
                                                    />
                                                </td>
                                                <td className="border px-4 py-2" style={{ minWidth: '150px' }}>
                                                    <input
                                                        type="text"
                                                        name="dni"
                                                        value={occupant.dni}
                                                        onChange={(e) => handleOccupantChange(index, e)}
                                                        className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                                    />
                                                </td>
                                                <td className="border px-4 py-2">
                                                    <input
                                                        type="text"
                                                        name="condicion"
                                                        value={occupant.condicion}
                                                        disabled
                                                        className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-500 bg-gray-200 cursor-not-allowed"
                                                    />
                                                </td>
                                                <td className="border px-4 py-2">
                                                    <input
                                                        type="text"
                                                        name="codigo_habitacion"
                                                        value={occupant.codigo_habitacion}
                                                        disabled
                                                        className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-500 bg-gray-200 cursor-not-allowed"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="block md:hidden">
                                {editedOccupants.map((occupant, index) => (
                                    <div key={index} className="mb-4">
                                        <label className="block text-gray-700 text-sm font-bold mb-2">Nombre y Apellido:</label>
                                        <input
                                            type="text"
                                            name="apellido_nombre"
                                            value={occupant.apellido_nombre}
                                            onChange={(e) => handleOccupantChange(index, e)}
                                            className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                        />

                                        <label className="block text-gray-700 text-sm font-bold mb-2">Fecha de Nacimiento:</label>
                                        <input
                                            type="date"
                                            name="fecha_nac"
                                            disabled
                                            value={occupant.fecha_nac}
                                            onChange={(e) => handleOccupantChange(index, e)}
                                            className="w-full shadow appearance-none border bg-gray-200 rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                        />

                                        <label className="block text-gray-700 text-sm font-bold mb-2">DNI:</label>
                                        <input
                                            type="text"
                                            name="dni"
                                            value={occupant.dni}
                                            onChange={(e) => handleOccupantChange(index, e)}
                                            className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                                        />

                                        <label className="block text-gray-700 text-sm font-bold mb-2">Condición:</label>
                                        <input
                                            type="text"
                                            name="condicion"
                                            value={occupant.condicion}
                                            disabled
                                            className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-500 bg-gray-200 cursor-not-allowed mb-2"
                                        />

                                        <label className="block text-gray-700 text-sm font-bold mb-2">Código de Habitación:</label>
                                        <input
                                            type="text"
                                            name="codigo_habitacion"
                                            value={occupant.codigo_habitacion}
                                            disabled
                                            className="w-full shadow appearance-none border rounded py-2 px-3 text-gray-500 bg-gray-200 cursor-not-allowed"
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-center w-full mt-4">

                                <button
                                    type="button"
                                    className="bg-red-500 text-white px-4 py-2 rounded-full mr-4"
                                    onClick={() => setEditModalOpen(false)}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-full">
                                    Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <div className="w-full flex flex-col items-center justify-center" >

                <button
                    type="button"
                    className="bg-blue-600 text-white px-4 py-2 mb-4 rounded-full max-w-[200px]"
                    onClick={() => router('/account-savelists')}
                >
                    Volver a reservas
                </button>

                <p className="text-red-500 mb-4">Para cancelación de reserva debe comunicarse con turismo setia</p>
            </div>

        </>
    );
};

export default EditReservation;